import { NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatToolbar } from '@angular/material/toolbar';
import { Router } from '@angular/router';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { startWith } from 'rxjs';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { ProjectListBloc } from '@connect-services/client/blocs/project-list.bloc';
import { AbstractPanelComponent } from '@connect-services/client/components/abstract-panel/abstract-panel.component';
import { DataButtonComponent } from '@connect-services/client/components/data-icon/data-button.component';
import { OrganizationComponent } from '@connect-services/client/components/organization/organization.component';
import { ProfileIconComponent } from '@connect-services/client/components/profile-icon/profile-icon.component';
import { ProjectSelectComponent } from '@connect-services/client/components/project-select/project-select.component';
import { RouteNameEnum, RoutePathEnum } from '@connect-services/client/models/routes.model';
import { IconService } from '@connect-services/client/services/icon.service';
import { SecurityService } from '@connect-services/client/services/security.service';
import { ViewModeEnum, ViewModeService } from '@connect-services/client/services/view-mode.service';
import { ProjectStore } from '@connect-services/client/stores/project.store';
import { paginationMaxTake } from '@shared/client/models/pagination.model';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        DataButtonComponent,
        MatToolbar,
        NgOptimizedImage,
        OrganizationComponent,
        ProfileIconComponent,
        ProjectSelectComponent,
    ],
    selector: 'app-topbar',
    styleUrl: './topbar.component.scss',
    templateUrl: './topbar.component.html',
})
export class TopbarComponent extends AbstractPanelComponent implements OnInit {
    public icons!: Record<string, IconProp>;
    public readonly securityService = inject(SecurityService);
    public viewModeControl!: FormControl<ViewModeEnum>;
    public viewModeValues: ViewModeEnum[] = [ViewModeEnum.MAP, ViewModeEnum.LIST];

    protected readonly RouteNameEnum = RouteNameEnum;

    private readonly iconService = inject(IconService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly projectListBloc = inject(ProjectListBloc);
    private readonly projectStore = inject(ProjectStore);
    private readonly router = inject(Router);
    private readonly viewModeService = inject(ViewModeService);

    public constructor() {
        super();

        effect(async (): Promise<void> => {
            if (!this.securityService.meSignal()?.organization) {
                return;
            }

            await this.projectListBloc.load({
                orderBy: 'name',
                person: this.securityService.meSignal()!.id,
                take: paginationMaxTake,
            });
        });
    }

    public ngOnInit(): void {
        this.viewModeControl = new FormControl(this.viewModeService.activatedViewMode(), { nonNullable: true });

        this.viewModeControl.valueChanges.pipe(startWith(this.viewModeControl.value)).subscribe(() => {
            this.icons = this.iconService.buildIconsObject(this.viewModeValues, this.viewModeControl.value);

            this.viewModeService.activatedViewMode.set(this.viewModeControl.value);
        });
    }

    public async onLogoClick(event: MouseEvent): Promise<void> {
        event.preventDefault();

        await this.projectStore.refresh();

        const url = this.router.serializeUrl(
            this.router.createUrlTree([RoutePathEnum.PROJECTS, this.projectBloc.value()?.id]),
        );

        window.location.replace(url);
    }
}
